import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import API_URL from '../../config/config';
import { SetCursorToTop } from '../../utils/utils';
import WYSIWYGEditor from './WYSIWYGEditor';
import LeavePageDialog from '../common/LeavePageDialog';
import { Prompt } from 'react-router-dom';
import '../../styles/CME-Requirement-Management.css';

interface CreditTypeRow {
  id?: string;
  topicId: string;
  hours: string;
  frequency: string;
}

interface TopicRow {
  id?: string;
  topicId: string;
  hours: string;
  frequency: string;
}

interface EditRequirementsFormProps {
  boardId: string;
  onCancel: () => void;
  onSave: () => void;
  leaveRequested?: boolean;
  onLeaveHandled?: () => void;
}

const EditRequirementsForm: React.FC<EditRequirementsFormProps> = ({ boardId, onCancel, onSave, leaveRequested, onLeaveHandled }) => {
  // Board fields
  const [boardName, setBoardName] = useState('');
  const [website, setWebsite] = useState('');

  // General CME
  const [generalCMEId, setGeneralCMEId] = useState<string | undefined>();
  const [generalCMETopicId, setGeneralCMETopicId] = useState<string>('');
  const [generalCMEHours, setGeneralCMEHours] = useState('');
  const [licensurePeriod, setLicensurePeriod] = useState('');
  const [intervalValue, setIntervalValue] = useState<any>(null);

  // Description
  const [description, setDescription] = useState('');

  // Credit Types
  const [creditTypes, setCreditTypes] = useState<CreditTypeRow[]>([{ topicId: '', hours: '', frequency: '' }]);
  const [creditTypesNotApplicable, setCreditTypesNotApplicable] = useState(false);

  // Topic Requirements
  const [topicRequirements, setTopicRequirements] = useState<TopicRow[]>([{ topicId: '', hours: '', frequency: '' }]);
  const [topicReqsNotApplicable, setTopicReqsNotApplicable] = useState(false);

  // Topics from API
  const [creditTypeTopics, setCreditTypeTopics] = useState<any[]>([]);
  const [regularTopics, setRegularTopics] = useState<any[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'credit' | 'topic';
    index: number;
    id?: string;
  } | null>(null);

  const initialValuesRef = useRef<{
    boardName: string;
    website: string;
    generalCMEHours: string;
    licensurePeriod: string;
    intervalValue: any;
    description: string;
    creditTypes: CreditTypeRow[];
    creditTypesNotApplicable: boolean;
    topicRequirements: TopicRow[];
    topicReqsNotApplicable: boolean;
  } | null>(null);

  const isDirty = (): boolean => {
    const init = initialValuesRef.current;
    if (!init) return false;
    return (
      boardName !== init.boardName ||
      website !== init.website ||
      generalCMEHours !== init.generalCMEHours ||
      licensurePeriod !== init.licensurePeriod ||
      intervalValue !== init.intervalValue ||
      description !== init.description ||
      creditTypesNotApplicable !== init.creditTypesNotApplicable ||
      topicReqsNotApplicable !== init.topicReqsNotApplicable ||
      JSON.stringify(creditTypes) !== JSON.stringify(init.creditTypes) ||
      JSON.stringify(topicRequirements) !== JSON.stringify(init.topicRequirements)
    );
  };

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const descriptionRef = useRef(description);
  descriptionRef.current = description;

  useEffect(() => {
    loadData();
  }, [boardId]);

  // After data loads, wait one tick for ReactQuill to normalize the HTML via its
  // first onChange, then re-sync the snapshot so isDirty() doesn't false-positive.
  useEffect(() => {
    if (!loading && initialValuesRef.current) {
      const handle = setTimeout(() => {
        if (initialValuesRef.current) {
          initialValuesRef.current = { ...initialValuesRef.current, description: descriptionRef.current };
        }
      }, 0);
      return () => clearTimeout(handle);
    }
  }, [loading]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    if (leaveRequested) {
      if (isDirty()) {
        setCancelDialogOpen(true);
      } else {
        onCancel();
      }
    }
  }, [leaveRequested]);

  // Convert plain text (with \n) to Quill-compatible HTML — skips if already HTML
  const toEditorHtml = (text: string): string => {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    return text.split('\n').map(line => `<p>${line || '<br>'}</p>`).join('');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [boardRes, requirementsRes, topicsRes] = await Promise.all([
        axios.get(`${API_URL}/cmecertificationboard/${boardId}`),
        axios.get(`${API_URL}/management/requirements/board/${boardId}`),
        axios.get(`${API_URL}/cmetopics`)
      ]);

      // Board
      const boardData = (boardRes.data as any)?.certificationboard || (boardRes.data as any);
      setBoardName(boardData.Name || '');
      setWebsite(boardData.Website || '');

      // Topics
      const topics = (topicsRes.data as any)?.cmetopics?.rows || [];
      setCreditTypeTopics(topics.filter((t: any) => t.isCreditType || t.IsCreditType));
      setRegularTopics(topics.filter((t: any) => !(t.isCreditType || t.IsCreditType)));

      // Requirements
      const reqs = (requirementsRes.data as any)?.cmerequirements?.rows || [];

      // General CME
      const generalReq = reqs.find((r: any) => {
        const name = r.CME_Topic?.Name || '';
        return name.toLowerCase().includes('general') || name.toLowerCase() === 'none';
      });

      if (generalReq) {
        setGeneralCMEId(generalReq.Id);
        setGeneralCMETopicId(generalReq.CME_Topic_Id || '');
        const loadedHours = generalReq.CMEHours != null ? String(generalReq.CMEHours) : '';
        setGeneralCMEHours(loadedHours);
        setLicensurePeriod(generalReq.CMEHours === 0 ? '' : String(generalReq.Frequency || ''));
        setIntervalValue(generalReq.CMEHours === 0 ? null : generalReq.Interval);
        setDescription(toEditorHtml(generalReq.Description || ''));
      } else {
        setDescription('');
      }

      // Credit Types
      const creditReqs = reqs.filter((r: any) => r.CME_Topic?.isCreditType || r.CME_Topic?.IsCreditType);
      if (creditReqs.length > 0) {
        setCreditTypes(creditReqs.map((r: any) => ({
          id: r.Id,
          topicId: r.CME_Topic_Id || '',
          hours: String(r.CMEHours || ''),
          frequency: String(r.Frequency || '')
        })));
        setCreditTypesNotApplicable(false);
      } else {
        setCreditTypes([{ topicId: '', hours: '', frequency: '' }]);
        setCreditTypesNotApplicable(true);
      }

      // Topic Requirements — sort by createdAt ASC so newly added ones appear last
      const topicReqs = reqs
        .filter((r: any) => {
          const name = r.CME_Topic?.Name || '';
          const isGeneral = name.toLowerCase().includes('general') || name.toLowerCase() === 'none';
          const isCreditType = r.CME_Topic?.isCreditType || r.CME_Topic?.IsCreditType;
          return !isGeneral && !isCreditType;
        })
        .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      const initialTopicRequirements = topicReqs.length > 0
        ? topicReqs.map((r: any) => ({ id: r.Id, topicId: r.CME_Topic_Id || '', hours: String(r.CMEHours || ''), frequency: String(r.Frequency || '') }))
        : [{ topicId: '', hours: '', frequency: '' }];

      if (topicReqs.length > 0) {
        setTopicRequirements(initialTopicRequirements);
        setTopicReqsNotApplicable(false);
      } else {
        setTopicRequirements([{ topicId: '', hours: '', frequency: '' }]);
        setTopicReqsNotApplicable(true);
      }

      const initialCreditTypes = creditReqs.length > 0
        ? creditReqs.map((r: any) => ({ id: r.Id, topicId: r.CME_Topic_Id || '', hours: String(r.CMEHours || ''), frequency: String(r.Frequency || '') }))
        : [{ topicId: '', hours: '', frequency: '' }];

      initialValuesRef.current = {
        boardName: boardData.Name || '',
        website: boardData.Website || '',
        generalCMEHours: generalReq ? (generalReq.CMEHours != null ? String(generalReq.CMEHours) : '') : '',
        licensurePeriod: generalReq && generalReq.CMEHours !== 0 ? String(generalReq.Frequency || '') : '',
        intervalValue: generalReq ? generalReq.Interval : null,
        description: generalReq ? toEditorHtml(generalReq.Description || '') : '',
        creditTypes: initialCreditTypes,
        creditTypesNotApplicable: creditReqs.length === 0,
        topicRequirements: initialTopicRequirements,
        topicReqsNotApplicable: topicReqs.length === 0,
      };
    } catch (err: any) {
      console.error('Error loading edit form data:', err);
      console.error('Error details:', err?.response?.status, err?.response?.data, err?.config?.url);
      setSaveError(`Failed to load form data: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const isValidUrl = (url: string) => {
    if (!/^https?:\/\//i.test(url)) return false;
    try {
      new URL(url);
      return true;
    } catch { return false; }
  };

  const validate = (): boolean => {
    const newErrors: any = {};

    if (!boardName.trim()) {
      newErrors.boardName = 'Licensing Board Name is required';
    }

    if (generalCMEHours === '' || !Number.isInteger(Number(generalCMEHours)) || Number(generalCMEHours) < 0) {
      newErrors.generalCMEHours = 'Hours must be a whole number (0 or greater)';
    }
    if (Number(generalCMEHours) !== 0) {
      if (!licensurePeriod || !Number.isInteger(Number(licensurePeriod)) || Number(licensurePeriod) <= 0) {
        newErrors.licensurePeriod = 'Licensure Period must be a whole number greater than 0';
      }
    }

    const descriptionText = description.replace(/<[^>]*>/g, '').trim();
    if (!descriptionText) {
      newErrors.description = 'Requirement Description is required';
    }

    if (!website.trim()) {
      newErrors.website = 'Website is required';
    } else if (!isValidUrl(website)) {
      newErrors.website = 'Please enter a valid web address';
    }

    if (!creditTypesNotApplicable) {
      const creditErrors: any = {};
      const seenTopics = new Set<string>();
      creditTypes.forEach((ct, i) => {
        const rowErrors: any = {};
        if (!ct.topicId) {
          rowErrors.topicId = 'Credit type is required';
        } else if (seenTopics.has(ct.topicId)) {
          rowErrors.topicId = 'Please choose a different option';
        } else {
          seenTopics.add(ct.topicId);
        }
        if (!ct.hours || isNaN(parseFloat(ct.hours)) || parseFloat(ct.hours) <= 0) {
          rowErrors.hours = 'Required';
        }
        if (!ct.frequency || !Number.isInteger(Number(ct.frequency)) || Number(ct.frequency) <= 0) {
          rowErrors.frequency = 'Required';
        }
        if (Object.keys(rowErrors).length > 0) creditErrors[i] = rowErrors;
      });
      if (Object.keys(creditErrors).length > 0) newErrors.creditTypes = creditErrors;
    }

    if (!topicReqsNotApplicable) {
      const topicErrors: any = {};
      const seenTopics = new Set<string>();
      topicRequirements.forEach((tr, i) => {
        const rowErrors: any = {};
        if (!tr.topicId) {
          rowErrors.topicId = 'Topic is required';
        } else if (seenTopics.has(tr.topicId)) {
          rowErrors.topicId = 'Please choose a different option';
        } else {
          seenTopics.add(tr.topicId);
        }
        if (!tr.hours || isNaN(parseFloat(tr.hours)) || parseFloat(tr.hours) <= 0) {
          rowErrors.hours = 'Required';
        }
        if (!tr.frequency || !Number.isInteger(Number(tr.frequency)) || Number(tr.frequency) <= 0) {
          rowErrors.frequency = 'Required';
        }
        if (Object.keys(rowErrors).length > 0) topicErrors[i] = rowErrors;
      });
      if (Object.keys(topicErrors).length > 0) newErrors.topicRequirements = topicErrors;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstError = document.querySelector('.cme-requirement-error-message');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setSaveError(null);

    try {
      // 1. Update board name + website
      await axios.put(`${API_URL}/cmecertificationboard/${boardId}`, {
        Name: boardName.trim(),
        Website: website.trim()
      });

      // 2. General CME
      {
        const generalPayload = {
          CMEHours: parseInt(generalCMEHours),
          Frequency: Number(generalCMEHours) === 0 ? null : parseInt(licensurePeriod),
          Interval: Number(generalCMEHours) === 0 ? null : intervalValue,
          Description: description,
          CME_Topic_Id: generalCMETopicId
        };
        if (generalCMEId) {
          await axios.put(`${API_URL}/management/requirements/${generalCMEId}`, generalPayload);
        } else {
          await axios.post(`${API_URL}/management/requirements`, {
            ...generalPayload,
            certificationBoardId: boardId
          });
        }
      }

      // 3. Credit Types
      if (creditTypesNotApplicable) {
        const toDelete = creditTypes.filter(ct => ct.id);
        await Promise.all(toDelete.map(ct => axios.delete(`${API_URL}/management/requirements/${ct.id}`)));
      } else {
        await Promise.all(creditTypes.map(ct => {
          const payload = {
            CMEHours: parseFloat(ct.hours),
            Frequency: parseInt(ct.frequency) || 1,
            Interval: intervalValue,
            CME_Topic_Id: ct.topicId
          };
          if (ct.id) {
            return axios.put(`${API_URL}/management/requirements/${ct.id}`, payload);
          } else {
            return axios.post(`${API_URL}/management/requirements`, { ...payload, certificationBoardId: boardId });
          }
        }));
      }

      // 4. Topic Requirements
      if (topicReqsNotApplicable) {
        const toDelete = topicRequirements.filter(tr => tr.id);
        await Promise.all(toDelete.map(tr => axios.delete(`${API_URL}/management/requirements/${tr.id}`)));
      } else {
        await Promise.all(topicRequirements.map(tr => {
          const payload = {
            CMEHours: parseFloat(tr.hours),
            Frequency: parseInt(tr.frequency) || 1,
            Interval: intervalValue,
            CME_Topic_Id: tr.topicId
          };
          if (tr.id) {
            return axios.put(`${API_URL}/management/requirements/${tr.id}`, payload);
          } else {
            return axios.post(`${API_URL}/management/requirements`, { ...payload, certificationBoardId: boardId });
          }
        }));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      SetCursorToTop();
      await loadData();
    } catch (err: any) {
      console.error('Error saving:', err);
      const msg = err.response?.data?.message || 'Failed to save. Please try again.';
      setSaveError(msg);
      setTimeout(() => setSaveError(null), 4000);
      SetCursorToTop();
    } finally {
      setSaving(false);
    }
  };

  // Credit Type handlers
  const handleAddCreditType = () => {
    setCreditTypes([...creditTypes, { topicId: '', hours: '', frequency: '' }]);
  };

  const handleCreditTypeChange = (index: number, field: 'topicId' | 'hours' | 'frequency', value: string) => {
    if (field === 'hours' && value !== '' && (!/^\d*\.?\d*$/.test(value) || parseFloat(value) <= 0)) return;
    if (field === 'frequency' && value !== '' && (!/^\d+$/.test(value) || parseInt(value) <= 0)) return;
    const updated = [...creditTypes];
    updated[index] = { ...updated[index], [field]: value };
    setCreditTypes(updated);
    if (errors.creditTypes?.[index]?.[field]) {
      const newErrors = { ...errors };
      delete newErrors.creditTypes[index][field];
      setErrors(newErrors);
    }
  };

  const confirmDeleteCreditType = (index: number) => {
    setDeleteDialog({ open: true, type: 'credit', index, id: creditTypes[index].id });
  };

  const handleDeleteCreditType = async () => {
    if (!deleteDialog) return;
    const { index, id } = deleteDialog;
    if (id) {
      try { await axios.delete(`${API_URL}/management/requirements/${id}`); }
      catch (err) { console.error('Error deleting credit type:', err); }
    }
    setCreditTypes(creditTypes.filter((_, i) => i !== index));
    setDeleteDialog(null);
  };

  // Topic Requirement handlers
  const handleAddTopicRequirement = () => {
    setTopicRequirements([...topicRequirements, { topicId: '', hours: '', frequency: '' }]);
  };

  const handleTopicRequirementChange = (index: number, field: 'topicId' | 'hours' | 'frequency', value: string) => {
    if (field === 'hours' && value !== '' && (!/^\d*\.?\d*$/.test(value) || parseFloat(value) <= 0)) return;
    if (field === 'frequency' && value !== '' && (!/^\d+$/.test(value) || parseInt(value) <= 0)) return;
    const updated = [...topicRequirements];
    updated[index] = { ...updated[index], [field]: value };
    setTopicRequirements(updated);
    if (errors.topicRequirements?.[index]?.[field]) {
      const newErrors = { ...errors };
      delete newErrors.topicRequirements[index][field];
      setErrors(newErrors);
    }
  };

  const confirmDeleteTopicRequirement = (index: number) => {
    setDeleteDialog({ open: true, type: 'topic', index, id: topicRequirements[index].id });
  };

  const handleDeleteTopicRequirement = async () => {
    if (!deleteDialog) return;
    const { index, id } = deleteDialog;
    if (id) {
      try { await axios.delete(`${API_URL}/management/requirements/${id}`); }
      catch (err) { console.error('Error deleting topic requirement:', err); }
    }
    setTopicRequirements(topicRequirements.filter((_, i) => i !== index));
    setDeleteDialog(null);
  };

  const sectionTitleSx = {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1976D2'
  };

  const fieldLabelSx = {
    fontSize: '16px',
    fontWeight: 400,
    color: '#09101D',
    opacity: 0.8,
    mb: 0.5
  };

  const columnHeaderSx = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#444'
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: '44px',
      borderRadius: '100px',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#DDDDDD',
        borderWidth: '1px',
        top: 0,
        '& legend': { display: 'none' }
      },
      '&:hover fieldset': { borderColor: '#1976D2', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: '#1976D2', borderWidth: '1px' }
    },
    '& .MuiInputBase-input': {
      padding: '10px 16px'
    }
  };

  const noBorderInput = { className: 'no-border-input' };

  const textareaSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      '& fieldset': { borderColor: '#DDDDDD' },
      '&:hover fieldset': { borderColor: '#1976D2' },
      '&.Mui-focused fieldset': { borderColor: '#1976D2' }
    }
  };

  const selectSx = {
    borderRadius: '100px',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#DDDDDD' },
    '&:hover fieldset': { borderColor: '#1976D2' },
    '&.Mui-focused fieldset': { borderColor: '#1976D2' }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '24px', backgroundColor: '#FFFFFF', textAlign: 'left' }}>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
          CME requirement saved successfully.
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {/* Board Name Heading */}
      <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#09101D', mb: 3 }}>
        {boardName}
      </Typography>

      {/* Section 1: Licensing Board Name */}
      <Box sx={{ mb: 4 }}>
        <div className="cme-requirement-field-box">
          <label className="cme-requirement-field-label required">Licensing Board Name</label>
          <input
            type="text"
            value={boardName}
            onChange={(e) => {
              setBoardName(e.target.value);
              if (errors.boardName) setErrors({ ...errors, boardName: undefined });
            }}
            className={errors.boardName ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
          />
          {errors.boardName && (
            <div className="cme-requirement-error-message">
              <ErrorIcon />
              <span>{errors.boardName}</span>
            </div>
          )}
        </div>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Section 2: General CME Requirements */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={sectionTitleSx}>General CME Requirements</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
            <div className="cme-requirement-field-box" style={{ flex: 1 }}>
              <label className="cme-requirement-field-label">Hours</label>
              <input
                type="number"
                value={generalCMEHours}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (/^\d+$/.test(val) && parseInt(val) >= 0)) {
                    setGeneralCMEHours(val);
                    if (parseInt(val) === 0) {
                      setLicensurePeriod('');
                      setErrors((prev: any) => ({ ...prev, generalCMEHours: undefined, licensurePeriod: undefined }));
                    } else if (errors.generalCMEHours) {
                      setErrors({ ...errors, generalCMEHours: undefined });
                    }
                  }
                }}
                min={0}
                step={1}
                onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                className={errors.generalCMEHours ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
              />
              {errors.generalCMEHours && (
                <div className="cme-requirement-error-message">
                  <ErrorIcon />
                  <span>{errors.generalCMEHours}</span>
                </div>
              )}
            </div>
            <div className="cme-requirement-field-box" style={{ flex: 1 }}>
              <label className="cme-requirement-field-label">Licensure Period (Year)</label>
              <input
                type="number"
                value={licensurePeriod}
                disabled={generalCMEHours === '0'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (/^\d+$/.test(val) && parseInt(val) > 0)) {
                    setLicensurePeriod(val);
                    if (errors.licensurePeriod) setErrors({ ...errors, licensurePeriod: undefined });
                  }
                }}
                min={1}
                step={1}
                onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                className={errors.licensurePeriod ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
              />
              {errors.licensurePeriod && (
                <div className="cme-requirement-error-message">
                  <ErrorIcon />
                  <span>{errors.licensurePeriod}</span>
                </div>
              )}
            </div>
          </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Section 3: Credit Types */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={sectionTitleSx}>Credit Types</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={creditTypesNotApplicable}
                onChange={(e) => {
                  setCreditTypesNotApplicable(e.target.checked);
                  if (e.target.checked) {
                    setErrors((prev: any) => ({ ...prev, creditTypes: undefined }));
                  }
                }}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: '13px', color: '#444' }}>Not Applicable</Typography>}
          />
        </Box>

        {!creditTypesNotApplicable && (
          <>
            {/* Column Headers */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Type</Typography>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Hours</Typography>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Licensure Period (Year)</Typography>
              </Box>
              <Box sx={{ minWidth: '48px' }} />
            </Box>

            {/* Rows */}
            {creditTypes.map((ct, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <FormControl size="small" fullWidth error={!!errors.creditTypes?.[index]?.topicId}>
                      <Select
                        value={ct.topicId}
                        displayEmpty
                        onChange={(e) => handleCreditTypeChange(index, 'topicId', e.target.value)}
                        sx={selectSx}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {creditTypeTopics.map((t: any) => (
                          <MenuItem key={t.Id} value={t.Id}>{t.Name}</MenuItem>
                        ))}
                      </Select>
                      {errors.creditTypes?.[index]?.topicId && (
                        <FormHelperText>{errors.creditTypes[index].topicId}</FormHelperText>
                      )}
                    </FormControl>
                  </div>

                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={ct.hours}
                      onChange={(e) => handleCreditTypeChange(index, 'hours', e.target.value)}
                      min={0.01}
                      step={0.01}
                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                      className={errors.creditTypes?.[index]?.hours ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
                    />
                    {errors.creditTypes?.[index]?.hours && (
                      <div className="cme-requirement-error-message">
                        <ErrorIcon />
                        <span>{errors.creditTypes[index].hours}</span>
                      </div>
                    )}
                  </div>

                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={ct.frequency}
                      onChange={(e) => handleCreditTypeChange(index, 'frequency', e.target.value)}
                      min={1}
                      step={1}
                      onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                      className={errors.creditTypes?.[index]?.frequency ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
                    />
                    {errors.creditTypes?.[index]?.frequency && (
                      <div className="cme-requirement-error-message">
                        <ErrorIcon />
                        <span>{errors.creditTypes[index].frequency}</span>
                      </div>
                    )}
                  </div>
                </Box>

                <Box sx={{ minWidth: '48px', display: 'flex', alignItems: 'center', pt: 0.5 }}>
                  {index > 0 && (
                    <div className="CMETopicDelelteButton" onClick={() => confirmDeleteCreditType(index)} style={{ cursor: 'pointer' }}>
                      <DeleteIcon sx={{ fontSize: '16px', color: '#FFFFFF' }} />
                    </div>
                  )}
                </Box>
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddCreditType}
                    size="small"
                    sx={{ textTransform: 'none', color: '#1976D2', fontWeight: 600, mt: 0.5, border: '1px solid #1976D2', borderRadius: '50px' }}
                  >
                    Add Credit Type
                  </Button>
                </Box>
              </Box>
              <Box sx={{ minWidth: '48px' }} />
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Section 4: Topic Requirements */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={sectionTitleSx}>Topic Requirements</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={topicReqsNotApplicable}
                onChange={(e) => {
                  setTopicReqsNotApplicable(e.target.checked);
                  if (e.target.checked) {
                    setErrors((prev: any) => ({ ...prev, topicRequirements: undefined }));
                  }
                }}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: '13px', color: '#444' }}>Not Applicable</Typography>}
          />
        </Box>

        {!topicReqsNotApplicable && (
          <>
            {/* Column Headers */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Topic</Typography>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Hours</Typography>
                <Typography sx={{ ...columnHeaderSx, flex: 1 }}>Licensure Period (Year)</Typography>
              </Box>
              <Box sx={{ minWidth: '48px' }} />
            </Box>

            {/* Rows */}
            {topicRequirements.map((tr, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <FormControl size="small" fullWidth error={!!errors.topicRequirements?.[index]?.topicId}>
                      <Select
                        value={tr.topicId}
                        displayEmpty
                        onChange={(e) => handleTopicRequirementChange(index, 'topicId', e.target.value)}
                        sx={selectSx}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {regularTopics.map((t: any) => (
                          <MenuItem key={t.Id} value={t.Id}>{t.Name}</MenuItem>
                        ))}
                      </Select>
                      {errors.topicRequirements?.[index]?.topicId && (
                        <FormHelperText>{errors.topicRequirements[index].topicId}</FormHelperText>
                      )}
                    </FormControl>
                  </div>

                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={tr.hours}
                      onChange={(e) => handleTopicRequirementChange(index, 'hours', e.target.value)}
                      min={0.01}
                      step={0.01}
                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                      className={errors.topicRequirements?.[index]?.hours ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
                    />
                    {errors.topicRequirements?.[index]?.hours && (
                      <div className="cme-requirement-error-message">
                        <ErrorIcon />
                        <span>{errors.topicRequirements[index].hours}</span>
                      </div>
                    )}
                  </div>

                  <div className="cme-requirement-field-box" style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={tr.frequency}
                      onChange={(e) => handleTopicRequirementChange(index, 'frequency', e.target.value)}
                      min={1}
                      step={1}
                      onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                      className={errors.topicRequirements?.[index]?.frequency ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
                    />
                    {errors.topicRequirements?.[index]?.frequency && (
                      <div className="cme-requirement-error-message">
                        <ErrorIcon />
                        <span>{errors.topicRequirements[index].frequency}</span>
                      </div>
                    )}
                  </div>
                </Box>

                <Box sx={{ minWidth: '48px', display: 'flex', alignItems: 'center', pt: 0.5 }}>
                  {index > 0 && (
                    <div className="CMETopicDelelteButton" onClick={() => confirmDeleteTopicRequirement(index)} style={{ cursor: 'pointer' }}>
                      <DeleteIcon sx={{ fontSize: '16px', color: '#FFFFFF' }} />
                    </div>
                  )}
                </Box>
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flex: 1, gap: '16px' }}>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddTopicRequirement}
                    size="small"
                    sx={{ textTransform: 'none', color: '#1976D2', fontWeight: 600, mt: 0.5, border: '1px solid #1976D2', borderRadius: '50px' }}
                  >
                    Add Topic
                  </Button>
                </Box>
              </Box>
              <Box sx={{ minWidth: '48px' }} />
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Section 5: Additional Information */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Additional Information</Typography>

        <Box sx={{ mb: 3 }}>
          <div className="cme-requirement-field-box">
            <label className="cme-requirement-field-label">Website</label>
            <input
              type="text"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                if (errors.website) setErrors({ ...errors, website: undefined });
              }}
              placeholder="https://example.com"
              className={errors.website ? 'cme-requirement-input-box no-border-input error' : 'cme-requirement-input-box no-border-input'}
            />
            {errors.website && (
              <div className="cme-requirement-error-message">
                <ErrorIcon />
                <span>{errors.website}</span>
              </div>
            )}
          </div>
        </Box>

        <div className="cme-requirement-field-box">
          <label className="cme-requirement-field-label">Requirement Description</label>
          <WYSIWYGEditor
            value={description}
            onChange={(val) => { setDescription(val); if (errors.description) setErrors({ ...errors, description: undefined }); }}
            placeholder="Enter requirement description..."
          />
          {errors.description && (
            <div className="cme-requirement-error-message">
              <ErrorIcon />
              <span>{errors.description}</span>
            </div>
          )}
        </div>
      </Box>

      {/* Footer Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => { if (isDirty()) setCancelDialogOpen(true); else onCancel(); }}
          disabled={saving}
          sx={{ textTransform: 'none', borderRadius: '50px', padding: '8px 24px', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', borderRadius: '50px', padding: '8px 24px', fontWeight: 600 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <LeavePageDialog
        open={cancelDialogOpen}
        onLeave={onCancel}
        onStay={() => { setCancelDialogOpen(false); if (onLeaveHandled) onLeaveHandled(); }}
      />
      <Prompt when={isDirty()} message="unsaved" />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog?.open} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: '8px' }}>
          <Button
            onClick={() => setDeleteDialog(null)}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: '50px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={deleteDialog?.type === 'credit' ? handleDeleteCreditType : handleDeleteTopicRequirement}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', borderRadius: '50px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditRequirementsForm;
